import { useCallback, useEffect, useState } from 'react';
import { usePlatformUserAvatarUpload } from 'src/graphql/entities/platform-user/use-platform-user-avatar-upload';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { resizeImageToWebP } from './resize-image';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export enum AvatarFlushStatus {
  Noop = 'noop',
  Uploaded = 'uploaded',
  Failed = 'failed',
}

export enum AvatarFlushFailureReason {
  /** Upload was attempted and failed; uploadAvatar already surfaced an alert to the user. */
  UploadError = 'upload-error',
  /** A file was picked but we had no partnerId to upload it under. No alert was shown. */
  MissingPartner = 'missing-partner',
}

export type AvatarFlushResult =
  | { status: AvatarFlushStatus.Noop }
  | { status: AvatarFlushStatus.Uploaded; fileKey: string }
  | { status: AvatarFlushStatus.Failed; reason: AvatarFlushFailureReason };

export interface AvatarSelection {
  previewUrl: string | undefined;
  hasPendingWork: boolean;
  onFileChange: (file: File) => void;
  flush: (partnerId: number | null | undefined) => Promise<AvatarFlushResult>;
  reset: () => void;
}

export function useAvatarSelection(): AvatarSelection {
  const { uploadAvatar } = usePlatformUserAvatarUpload();
  const { showErrorAlert } = useNotifications();

  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [resizingPromise, setResizingPromise] = useState<Promise<File> | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!resizingPromise) {
      return;
    }
    let cancelled = false;
    resizingPromise.then(
      (processed) => {
        if (cancelled) {
          return;
        }
        setPendingFile(processed);
        setResizingPromise(null);
      },
      () => {
        if (cancelled) {
          return;
        }
        showErrorAlert('Could not process image. Try a different file.');
        setPreviewUrl(undefined);
        setPendingFile(null);
        setResizingPromise(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [resizingPromise, showErrorAlert]);

  const onFileChange = useCallback(
    (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        showErrorAlert('Unsupported image type. Use JPG, PNG, or WEBP.');
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        showErrorAlert('Image is too large. Maximum size is 5 MB.');
        return;
      }

      setPreviewUrl(URL.createObjectURL(file));
      setPendingFile(null);
      const resizePromise = resizeImageToWebP(file).then(
        ({ blob, fileName }) =>
          new File([blob], fileName, { type: 'image/webp', lastModified: Date.now() }),
      );
      setResizingPromise(resizePromise);
    },
    [showErrorAlert],
  );

  const reset = useCallback(() => {
    setPreviewUrl(undefined);
    setPendingFile(null);
    setResizingPromise(null);
  }, []);

  const hasPendingWork = pendingFile !== null || resizingPromise !== null;

  const flush = useCallback(
    async (partnerId: number | null | undefined): Promise<AvatarFlushResult> => {
      if (!hasPendingWork) {
        return { status: AvatarFlushStatus.Noop };
      }
      if (partnerId === null || partnerId === undefined) {
        return {
          status: AvatarFlushStatus.Failed,
          reason: AvatarFlushFailureReason.MissingPartner,
        };
      }
      let file = pendingFile;
      if (resizingPromise) {
        file = await resizingPromise.catch(() => null);
      }
      if (!file) {
        return {
          status: AvatarFlushStatus.Failed,
          reason: AvatarFlushFailureReason.UploadError,
        };
      }
      const fileKey = await uploadAvatar(file, partnerId);
      if (!fileKey) {
        return {
          status: AvatarFlushStatus.Failed,
          reason: AvatarFlushFailureReason.UploadError,
        };
      }
      return { status: AvatarFlushStatus.Uploaded, fileKey };
    },
    [hasPendingWork, pendingFile, resizingPromise, uploadAvatar],
  );

  return { previewUrl, hasPendingWork, onFileChange, flush, reset };
}
